// Support — SOS alerts and support tickets in one Messenger-style inbox
// (BL-16, 0315; Leon 2026-10-10; Messenger layout from the Oct 11 meeting).
//
// Left: queue tabs (Open, Unassigned, Mine, Urgent, Resolved, All — each with a
// count), search, a Filters popover (category, status, hub, sort) and the
// conversations, urgent pinned on top, unread marked. Right: the whole thread,
// with claim / resolve / reopen (0335) in its header and the composer at the
// bottom. The same unread count sits on the Support nav item (SupportNavBadge).
// A hub coordinator sees their hub; admin, executive and superadmin see
// everything — the view and RLS decide, not this file.
//
// The paid-but-cancelled list is admin/superadmin only: it shows amounts, and
// 0285 keeps staff money away from hub coordinators. It is always urgent and
// not attached to a ticket.
//
// ⚠ DATA WALL: names here are first names only, straight from the view.

import { HubSelect } from "@/components/HubSelect";
import { Avatar, AvatarBadge, AvatarFallback } from "@bednbody/ui/components/avatar";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bednbody/ui/components/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@bednbody/ui/components/empty";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@bednbody/ui/components/input-group";
import { StatTile } from "@bednbody/ui/components/stat-tile";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { Popover, PopoverContent, PopoverTrigger } from "@bednbody/ui/components/popover";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import { formatPHP, formatTimeAgo } from "@bednbody/ui/lib/format";
import { cn } from "@bednbody/ui/lib/utils";
import {
  ChatsCircleIcon,
  FunnelSimpleIcon,
  MagnifyingGlassIcon,
  UserCheckIcon,
  UserCircleDashedIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { useHubsList } from "@/features/ops-dashboard/ops-dashboard.hooks";
import { AcknowledgeSosDialog, ResolveSosDialog } from "@/features/sos/SosDialogs";
import { useOpenSosAlerts } from "@/features/sos/sos.hooks";
import { useAuth } from "@/lib/auth";
import { DismissPaidItemDialog } from "./DismissPaidItemDialog";
import {
  type PaidWithoutBookingRow,
  usePaidWithoutBooking,
  useSeenThreads,
  useSupportTickets,
} from "./support.hooks";
import {
  CATEGORY_LABEL,
  DEFAULT_CRITERIA,
  filterInbox,
  type InboxCriteria,
  isClosed,
  type InboxItem,
  isUnread,
  type Queue,
  QUEUES,
  queueCounts,
  STATUS_LABEL,
  type SupportCategory,
  ticketStatusLabel,
  type SupportStatus,
  toInboxItems,
} from "./support.model";
import { avatarTone, STATUS_TONE, TicketThread } from "./TicketThread";

/** Open / Resolved / All stay as tabs; the work queues are the cards above them. */
const TAB_QUEUES = QUEUES.filter(
  (q) => q.value === "open" || q.value === "resolved" || q.value === "all",
);

const QUEUE_CARDS: readonly {
  queue: Queue;
  label: string;
  hint: string;
  icon: Icon;
  /** The tile wears this tone when its queue is the active filter. */
  tone: "default" | "warning" | "danger";
  /** The number's colour, and the faint wash behind a queue with something in it. */
  ink: string;
  wash: string;
}[] = [
  {
    queue: "unassigned",
    label: "Unassigned",
    hint: "Nobody has these yet",
    icon: UserCircleDashedIcon,
    tone: "warning",
    ink: "text-warning",
    wash: "from-warning/10",
  },
  {
    queue: "mine",
    label: "Mine",
    hint: "You are handling",
    icon: UserCheckIcon,
    tone: "default",
    ink: "text-primary-dark",
    wash: "from-primary/10",
  },
  {
    queue: "urgent",
    label: "Urgent",
    hint: "Needs attention now",
    icon: WarningIcon,
    tone: "danger",
    ink: "text-destructive",
    wash: "from-destructive/10",
  },
];

export function SupportPage() {
  const { claims, session } = useAuth();
  const isAdmin = claims?.role === "admin" || claims?.role === "superadmin";
  const isSuperadmin = claims?.role === "superadmin";
  const tickets = useSupportTickets();
  const sos = useOpenSosAlerts();
  const paid = usePaidWithoutBooking(isAdmin);
  const { seen, markSeen } = useSeenThreads(session?.user.id);
  const hubs = useHubsList();

  const [criteria, setCriteria] = useState<InboxCriteria>(DEFAULT_CRITERIA);
  const [query, setQuery] = useState("");
  const myId = session?.user.id;
  const setCriterion = <K extends keyof InboxCriteria>(key: K, value: InboxCriteria[K]) =>
    setCriteria((current) => ({ ...current, [key]: value }));
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [sosId, setSosId] = useState<string | null>(null);
  const [dismissItem, setDismissItem] = useState<PaidWithoutBookingRow | null>(null);

  const items = useMemo(
    () =>
      toInboxItems(
        sos.data ?? [],
        tickets.data?.tickets ?? [],
        tickets.data?.bookingRefById ?? new Map(),
        tickets.data?.lastMessageByThread,
      ),
    [sos.data, tickets.data],
  );

  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      filterInbox(items, criteria, myId).filter(
        (item) =>
          needle === "" ||
          `${item.ref} ${item.who} ${CATEGORY_LABEL[item.category]} ${item.bookingRefs.join(" ")} ${
            item.lastMessage?.body ?? ""
          }`
            .toLowerCase()
            .includes(needle),
      ),
    [items, criteria, myId, needle],
  );
  const counts = useMemo(() => queueCounts(items, myId), [items, myId]);

  // A hub filter only helps someone who can see more than one hub. A hub
  // coordinator is already scoped to theirs by the view, so it is not offered.
  const hubOptions = useMemo(() => {
    const present = new Set(items.flatMap((item) => (item.hubId ? [item.hubId] : [])));
    return (hubs.data ?? []).filter((hub) => present.has(hub.id));
  }, [items, hubs.data]);
  const hubNameById = useMemo(
    () => new Map((hubs.data ?? []).map((hub) => [hub.id, hub.name])),
    [hubs.data],
  );
  const activeFilters = [
    criteria.category !== "",
    criteria.status !== "",
    criteria.hub !== "",
    criteria.sort !== "recent",
  ].filter(Boolean).length;

  // Urgent work is pinned to the top by the sort; the headings just say so. Only
  // drawn when both groups are on screen, so a calm inbox has no stray labels.
  const needsAttention = (item: InboxItem) =>
    item.urgent && !(item.kind === "ticket" && isClosed(item.ticket));
  const grouped =
    criteria.sort === "recent" &&
    visible.some(needsAttention) &&
    visible.some((item) => !needsAttention(item));

  const staffNameById = tickets.data?.staffNameById ?? new Map<string, string>();
  const openTicket = tickets.data?.tickets.find((t) => t.id === ticketId) ?? null;
  const openSos = sos.data?.find((a) => a.id === sosId) ?? null;
  const openItem = items.find((item) => item.kind === "ticket" && item.id === ticketId) ?? null;

  // Whatever is on screen counts as read — including a reply that lands while
  // the conversation is open.
  const openActivity = openItem?.lastActivity;
  useEffect(() => {
    if (ticketId && openActivity) markSeen(ticketId, openActivity);
  }, [ticketId, openActivity, markSeen]);

  function choose(item: InboxItem) {
    if (item.kind === "sos") {
      setSosId(item.id);
      return;
    }
    setTicketId(item.id);
    markSeen(item.id, item.lastActivity);
  }

  const failed = tickets.isError || sos.isError;
  const loading = tickets.isLoading || sos.isLoading;

  return (
    <div className="space-y-8">
      <PageHeader
        description="SOS alerts and support tickets, urgent first. Clients are shown by first name only."
        title="Support"
      />

      {isAdmin && (paid.data?.length ?? 0) > 0 ? (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle>Paid, cancelled, nothing credited</CardTitle>
            <CardDescription>
              Each of these was paid, then every booking on it was cancelled with no Credits issued.
              Always urgent.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {paid.data?.map((row) => (
                <li
                  key={row.payment_id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
                >
                  <span className="font-mono">
                    {row.booking_refs?.length ? row.booking_refs.join(", ") : "No booking"}
                  </span>
                  <span className="tabular-nums font-medium">{formatPHP(row.amount)}</span>
                  <span className="text-muted-foreground">
                    Paid {row.paid_at ? formatTimeAgo(row.paid_at) : "—"}
                    {row.cancelled_at ? ` · cancelled ${formatTimeAgo(row.cancelled_at)}` : ""}
                  </span>
                  {isSuperadmin ? (
                    <Button onClick={() => setDismissItem(row)} size="sm" variant="outline">
                      Dismiss as test
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {failed ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm text-muted-foreground">
              The inbox could not be loaded. That is a failed read, not an empty inbox.
            </p>
            <RetryButton
              size="sm"
              onRetry={() => Promise.all([tickets.refetch(), sos.refetch()])}
            />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        {QUEUE_CARDS.map((card) => {
          const active = criteria.queue === card.queue;
          const count = counts[card.queue];
          return (
            <StatTile
              className={cn(
                "text-left",
                // A faint wash from the tile's own tone, shown only while the
                // queue has something in it. The selected state paints over it.
                count > 0 && !active ? `bg-linear-to-br ${card.wash} to-card` : null,
              )}
              detail={card.hint}
              icon={card.icon}
              key={card.queue}
              label={card.label}
              layout="compact"
              onClick={() => setCriterion("queue", active ? "open" : card.queue)}
              render={<button type="button" />}
              selected={active}
              tone={card.tone}
            >
              <span className={count > 0 ? card.ink : undefined}>{count}</span>
            </StatTile>
          );
        })}
      </div>

      <div className="grid h-[calc(100vh-23rem)] min-h-[32rem] overflow-hidden rounded-card border border-border bg-card shadow-card-quiet lg:grid-cols-[24rem_minmax(0,1fr)]">
        {/* ── the conversations ── */}
        <aside
          aria-label="Conversations"
          className={cn(
            "min-h-0 flex-col border-border lg:flex lg:border-r",
            openTicket ? "hidden" : "flex",
          )}
        >
          <div className="space-y-3 bg-linear-to-b from-primary/10 to-transparent p-4 pb-0">
            <div className="flex items-center gap-2">
              <InputGroup className="flex-1">
                <InputGroupAddon>
                  <MagnifyingGlassIcon />
                </InputGroupAddon>
                <InputGroupInput
                  aria-label="Search conversations"
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search tickets and messages"
                  type="search"
                  value={query}
                />
              </InputGroup>

              <Popover>
                <PopoverTrigger
                  render={
                    <Button
                      aria-label={
                        activeFilters > 0 ? `Filters, ${activeFilters} active` : "Filters"
                      }
                      className="relative"
                      size="icon-lg"
                      variant="outline"
                    />
                  }
                >
                  <FunnelSimpleIcon />
                  {activeFilters > 0 ? (
                    <span className="absolute -right-1.5 -top-1.5 grid size-5 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                      {activeFilters}
                    </span>
                  ) : null}
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 space-y-3">
                  <FilterField label="Category">
                    <NativeSelect
                      aria-label="Category"
                      className="w-full"
                      onChange={(event) =>
                        setCriterion("category", event.target.value as InboxCriteria["category"])
                      }
                      value={criteria.category}
                    >
                      <NativeSelectOption value="">Any category</NativeSelectOption>
                      {(["sos", "payment", "booking", "account", "other"] as const).map((value) => (
                        <NativeSelectOption key={value} value={value}>
                          {CATEGORY_LABEL[value as SupportCategory | "sos"]}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FilterField>
                  <FilterField label="Status">
                    <NativeSelect
                      aria-label="Status"
                      className="w-full"
                      onChange={(event) =>
                        setCriterion("status", event.target.value as SupportStatus | "")
                      }
                      value={criteria.status}
                    >
                      <NativeSelectOption value="">Any status</NativeSelectOption>
                      {(Object.keys(STATUS_LABEL) as SupportStatus[]).map((value) => (
                        <NativeSelectOption key={value} value={value}>
                          {STATUS_LABEL[value]}
                        </NativeSelectOption>
                      ))}
                    </NativeSelect>
                  </FilterField>
                  {hubOptions.length > 1 ? (
                    <FilterField label="Hub">
                      <HubSelect
                        ariaLabel="Hub"
                        className="w-full"
                        hubs={hubOptions}
                        value={criteria.hub}
                        onValueChange={(hubId) => setCriterion("hub", hubId ?? "")}
                      />
                    </FilterField>
                  ) : null}
                  <FilterField label="Sort by">
                    <NativeSelect
                      aria-label="Sort"
                      className="w-full"
                      onChange={(event) =>
                        setCriterion("sort", event.target.value as InboxCriteria["sort"])
                      }
                      value={criteria.sort}
                    >
                      <NativeSelectOption value="recent">Newest activity first</NativeSelectOption>
                      <NativeSelectOption value="oldest">Waiting longest first</NativeSelectOption>
                    </NativeSelect>
                  </FilterField>
                  <Button
                    className="w-full"
                    disabled={activeFilters === 0}
                    onClick={() =>
                      setCriteria((current) => ({ ...DEFAULT_CRITERIA, queue: current.queue }))
                    }
                    variant="ghost"
                  >
                    Clear filters
                  </Button>
                </PopoverContent>
              </Popover>
            </div>

            {/* Queues as underline tabs — what a support desk works from. */}
            <div
              aria-label="Queue"
              className="-mx-4 flex gap-1 overflow-x-auto border-b border-border px-4"
              role="tablist"
            >
              {TAB_QUEUES.map((queue) => {
                const active = criteria.queue === queue.value;
                return (
                  <button
                    key={queue.value}
                    aria-selected={active}
                    className={cn(
                      "-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold transition-colors focus-visible:outline-none",
                      active
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setCriterion("queue", queue.value)}
                    role="tab"
                    type="button"
                  >
                    {queue.label}
                    <span
                      className={cn(
                        "min-w-6 rounded-full px-1.5 text-center text-sm font-semibold tabular-nums",
                        active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                      )}
                    >
                      {counts[queue.value]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <ul className="min-h-0 flex-1 divide-y divide-border overflow-y-auto">
            {loading ? (
              <li className="p-6 text-sm text-muted-foreground">Loading conversations…</li>
            ) : visible.length === 0 ? (
              <li>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <ChatsCircleIcon />
                    </EmptyMedia>
                    <EmptyTitle>
                      {criteria.queue === "open" && activeFilters === 0 && needle === ""
                        ? "You're all caught up"
                        : "No conversations match"}
                    </EmptyTitle>
                    <EmptyDescription>
                      {criteria.queue === "open" && activeFilters === 0 && needle === ""
                        ? "Nothing needs attention. Resolved or All shows the rest."
                        : "Try another queue, or clear the filters."}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              </li>
            ) : (
              visible.map((item, index) => {
                const group = needsAttention(item) ? "attention" : "rest";
                const previous = visible[index - 1];
                const startsGroup =
                  grouped &&
                  (!previous || (needsAttention(previous) ? "attention" : "rest") !== group);
                return (
                  <Fragment key={`${item.kind}:${item.id}`}>
                    {startsGroup ? (
                      <li
                        className="sticky top-0 z-10 flex items-center gap-2 bg-muted px-4 py-2 text-sm font-semibold text-foreground"
                        role="presentation"
                      >
                        {group === "attention" ? (
                          <WarningIcon className="size-4 text-destructive" weight="fill" />
                        ) : null}
                        {group === "attention" ? "Needs attention" : "Everything else"}
                      </li>
                    ) : null}
                    <ConversationRow
                      item={item}
                      selected={item.kind === "ticket" && item.id === ticketId}
                      unread={isUnread(item, seen)}
                      onChoose={choose}
                    />
                  </Fragment>
                );
              })
            )}
          </ul>
        </aside>

        {/* ── the thread ── */}
        <div className={cn("min-h-0 lg:block", openTicket ? "block" : "hidden")}>
          {openTicket ? (
            <TicketThread
              ticket={openTicket}
              bookingRef={
                openTicket.booking_id
                  ? (tickets.data?.bookingRefById.get(openTicket.booking_id) ?? null)
                  : null
              }
              assignedName={
                openTicket.assigned_staff_id
                  ? (staffNameById.get(openTicket.assigned_staff_id) ?? null)
                  : null
              }
              canSetUrgency={isAdmin}
              hubName={openTicket.hub_id ? (hubNameById.get(openTicket.hub_id) ?? null) : null}
              isMine={
                openTicket.assigned_staff_id !== null && openTicket.assigned_staff_id === myId
              }
              onBack={() => setTicketId(null)}
            />
          ) : (
            <Empty className="h-full bg-linear-to-br from-primary/5 via-transparent to-primary-light/10">
              <EmptyHeader>
                <span className="grid size-20 place-items-center rounded-full bg-primary/10 text-primary ring-8 ring-primary/5">
                  <ChatsCircleIcon className="size-10" weight="duotone" />
                </span>
                <EmptyTitle className="text-xl font-bold">Select a conversation</EmptyTitle>
                <EmptyDescription>
                  Pick one on the left to read the thread, claim it and reply.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>
      </div>

      {dismissItem ? (
        <DismissPaidItemDialog
          item={dismissItem}
          open
          onOpenChange={(open) => (open ? null : setDismissItem(null))}
        />
      ) : null}

      {openSos ? (
        openSos.stage === "open" ? (
          <AcknowledgeSosDialog
            alert={openSos}
            open
            onOpenChange={(open) => (open ? null : setSosId(null))}
          />
        ) : (
          <ResolveSosDialog
            alert={openSos}
            open
            onOpenChange={(open) => (open ? null : setSosId(null))}
          />
        )
      ) : null}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}

function ConversationRow({
  item,
  selected,
  unread,
  onChoose,
}: {
  item: InboxItem;
  selected: boolean;
  unread: boolean;
  onChoose: (item: InboxItem) => void;
}) {
  const sosItem = item.kind === "sos";
  const status = item.kind === "ticket" ? item.ticket.status : null;
  const closedByClient =
    item.kind === "ticket" && item.ticket.withdrawn_at !== null && status !== "resolved";
  const over = item.kind === "ticket" && (status === "resolved" || closedByClient);
  const unassigned = item.kind === "ticket" && !over && item.assignedStaffId === null;
  const preview = item.lastMessage
    ? `${item.lastMessage.sender === "staff" ? "Team: " : ""}${item.lastMessage.body}`
    : item.statusLabel;

  return (
    <li>
      <button
        aria-current={selected ? "true" : undefined}
        className={cn(
          "flex w-full gap-3 border-l-4 p-4 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none",
          selected
            ? "border-primary bg-primary/10"
            : sosItem || (item.urgent && !over)
              ? "border-destructive"
              : over
                ? "border-transparent"
                : status === "waiting"
                  ? "border-warning"
                  : status === "open"
                    ? "border-primary/60"
                    : "border-transparent",
        )}
        onClick={() => onChoose(item)}
        type="button"
      >
        <Avatar>
          <AvatarFallback className={cn("font-bold", avatarTone(item.category, item.urgent))}>
            {sosItem ? (
              <WarningIcon className="size-5" weight="fill" />
            ) : (
              item.who.charAt(0).toUpperCase()
            )}
          </AvatarFallback>
          {unread ? <AvatarBadge aria-label="Unread" role="img" /> : null}
        </Avatar>

        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-baseline gap-2">
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-base",
                unread ? "font-bold text-foreground" : "font-semibold text-foreground",
              )}
            >
              {item.who}
            </span>
            <span className="shrink-0 text-sm font-medium text-muted-foreground">
              {formatTimeAgo(item.lastActivity)}
            </span>
          </span>
          <span className="truncate text-sm font-medium text-muted-foreground">
            <span className="font-mono">{item.ref}</span> · {CATEGORY_LABEL[item.category]}
          </span>
          <span
            className={cn(
              "line-clamp-2 text-sm",
              unread ? "font-medium text-foreground" : "text-muted-foreground",
            )}
          >
            {preview}
          </span>
          <span className="flex flex-wrap gap-1 pt-0.5">
            {sosItem ? (
              <Badge className="h-6 text-sm" shape="soft" variant="destructive">
                SOS
              </Badge>
            ) : item.kind === "ticket" && status ? (
              <Badge
                className="h-6 text-sm"
                shape="soft"
                variant={closedByClient ? "muted-soft" : STATUS_TONE[status]}
              >
                {ticketStatusLabel(item.ticket)}
              </Badge>
            ) : null}
            {item.urgent && !sosItem && !over ? (
              <Badge className="h-6 text-sm" shape="soft" variant="destructive-soft">
                {item.urgentLabel ?? "Urgent"}
              </Badge>
            ) : null}
            {unassigned ? (
              <Badge className="h-6 text-sm" shape="soft" variant="warning-soft">
                Unassigned
              </Badge>
            ) : null}
          </span>
        </span>
      </button>
    </li>
  );
}
